import type { CancelAction, CancellationRecord } from './cancellation.types';

/** The fifteen lifecycle states (design §5). Widened at the edge so an unrecognised
 *  backend value renders as itself rather than crashing the badge. */
export type AgreementStatus =
    | 'draft'
    | 'active'
    | 'on_hold'
    | 'suspended'
    | 'pending_cancellation'
    | 'cancelled'
    | 'cancellation_reversed'
    | 'pending_transfer'
    | 'transferred'
    | 'transfer_reversed'
    | 'pending_claim'
    | 'expired'
    | 'completed'
    | 'renewed'
    | 'dead';

export const AGREEMENT_STATUSES: AgreementStatus[] = [
    'draft',
    'active',
    'on_hold',
    'suspended',
    'pending_cancellation',
    'cancelled',
    'cancellation_reversed',
    'pending_transfer',
    'transferred',
    'transfer_reversed',
    'pending_claim',
    'expired',
    'completed',
    'renewed',
    'dead',
];

/** Frozen snapshot blobs are typed loosely and read through accessors — their
 *  keys are a historical record, NOT a live contract. See F4 / the two-tier rule
 *  in docs/plans/2026-08-01-agreement-detail-page.md. */
export type Snapshot = Record<string, unknown>;

/** A dealer's recorded receipt for a dealer-collected deposit, read on
 *  `GET agreements/{id}` via the `dealerPayments` relation. */
export interface DealerCollectedPayment {
    id: string;
    kind: 'deposit' | 'full_payment' | string;
    amount: string | null; // the server's exact DECIMAL(20,4) string
    reference: string | null;
    invoiceNumber: string | null;
    collectedAt: string | null; // Y-m-d
}

export type InvoicingRoute = 'open' | 'closed';

export interface Agreement {
    id: string;
    agreementRef: string;
    status: AgreementStatus | string;
    invoicingRoute?: InvoicingRoute;
    /** The latest cancellation, or null; undefined when the server sent none (service callers). */
    cancellation?: CancellationRecord | null;
    /** `actions.cancel`, absent on service-to-service reads. */
    cancelAction?: CancelAction;
    startDate: string | null; // ISO date; null until activation (F7)
    quoteId: string;
    customerId: string;
    vehicleId: string | null;
    dealerId: string;
    campaignId: string | null;
    productId: string | null;
    /** The resolved smr Product-level Plan. NOT renamed (decomposition doc:87-88). */
    planId: string | null;
    elemisAgreementRef: string | null;
    vin: string | null;
    totalCost: number | null; // major units, ex VAT, parsed from DECIMAL(20,4) string
    totalGross: number | null; // major units, inc VAT; null on agreements created before the gross carry
    feeNet: number | null;
    feeGross: number | null;
    createdAt: string | null;
    updatedAt: string | null;
    customerSnapshot: Snapshot;
    vehicleSnapshot: Snapshot;
    purchaseSnapshot: Snapshot;
    // payment_snapshot and campaign_snapshot are DELIBERATELY not mapped (F5).
    dealerPayments: DealerCollectedPayment[];
}

/** Agreements Slice 3 (#901). The successful response from
 *  `POST /agreements/elemis-applications` (3a, #900/#918) — `201` on a first
 *  attempt, `200` on an idempotent replay (`replayed: true`). Deliberately
 *  NOT the full `Agreement` shape: the caller already has the quote, and
 *  echoing customer/vehicle snapshots back would duplicate PII into a
 *  response the `audit` middleware forwards to audit-service. */
export interface ElemisApplication {
    applicationId: string;
    /** A CAPABILITY URL — see checkoutUrl.ts and useElemisCheckout's F-D4.
     *  Never persisted, never logged, never placed in the address bar. */
    checkoutUrl: string;
    agreementId: string;
    agreementRef: string;
    replayed: boolean;
}

/** NOTE (M3): PurchaseLine has NO `id` field — the row id (wire key `id`, `instanceId`
 *  in the domain type) collides across groups (F4), so it is never copied out of the blob. */
export type PurchaseAmount = number | string | null;

export interface PurchaseLine {
    key: string; // composite, index-based — see F4 (add_on ids are client numbers)
    name: string;
    description: string | null;
    net: PurchaseAmount; // major units
    gross: PurchaseAmount;
    startDate: string | null; // services/add-ons only
}

export interface PurchaseProduct {
    id: string | null;
    name: string | null;
    description: string | null;
}

/** Widened with `| string` at the edge, same as AgreementStatus: an unrecognised value
 *  renders as itself, and the selection util fails closed on anything but a literal match. */
export type SoldItemStatus = 'available' | 'claimed';
export type PricedAt = 'service' | 'item';

export interface SoldItem {
    id: string;
    name: string;
    description: string | null;
    code: string | null;
    quantity: number;
    unitNet: number; // major units, parsed from the 4dp wire string
    unitGross: number;
    /** The server's allocated share of the service price; absent when the list was not allocated. */
    lineNet?: number;
    lineGross?: number;
    /** A service's claim row; it is invoiced only with its whole service and never listed. */
    placeholder?: boolean;
    status: SoldItemStatus | string;
}

export interface SoldService {
    id: string;
    sequence: number;
    name: string;
    serviceType: string | null;
    dueDate: string | null; // Y-m-d
    dueMileage: number | null;
    pricedAt: PricedAt | string;
    net: number;
    gross: number;
    items: SoldItem[];
}

export interface SoldAddOn {
    id: string;
    name: string;
    description: string | null;
    quantity: number;
    unitNet: number;
    unitGross: number;
    linkedServiceId: string | null;
    dueDate: string | null;
    status: SoldItemStatus | string;
}

export interface SoldLines {
    services: SoldService[];
    addOns: SoldAddOn[];
}

export interface InvoicingCandidate {
    id: string;
    name: string;
}

export interface AgreementInvoiceLine {
    id: string;
    kind: 'service' | 'item' | string;
    name: string;
    quantity: number;
    net: number | null;
    gross: number | null;
    agreementItemIds: string[];
}

export type InvoiceFundingType = 'balance' | 'arrears' | 'pot_total';

export interface AgreementInvoice {
    id: string;
    agreementId: string;
    invoiceNumber: string;
    invoicedAt: string;
    invoicingDealerId: string;
    mileage: number | null;
    status: 'submitted' | 'reversed' | string;
    totalNet: number | null;
    totalGross: number | null;
    submittedBy: string;
    submittedAt: string;
    fundingType: InvoiceFundingType | null;
    claimedGross: number | null;
    shortfallGross: number | null;
    fundingApprovalId: string | null;
    reversedAt?: string | null;
    reversedBy?: string | null;
    reversedByName?: string | null;
    reversalReason?: string | null;
    lines: AgreementInvoiceLine[];
}

export interface SoldItemsQuery {
    data: SoldLines | undefined;
    isLoading: boolean;
    isError: boolean;
    refetch: () => unknown;
}

export interface AgreementInvoicesQuery {
    data: AgreementInvoice[] | undefined;
    isLoading: boolean;
    isError: boolean;
    refetch: () => unknown;
}

export type TopUpPosting = 'idle' | 'posting' | 'timedOut';

export interface AgreementCashPosition {
    cashPosition: number;
}

export interface AgreementCashPositionQuery {
    data: AgreementCashPosition | undefined;
    isLoading: boolean;
    isError: boolean;
}

export interface InvoicingCandidatesQuery {
    data: InvoicingCandidate[] | undefined;
    isLoading: boolean;
    isError: boolean;
}

export type InvoiceLinePayload =
    | { kind: 'service'; agreement_service_id: string }
    | { kind: 'item'; agreement_item_id: string };

export type FundingPurpose = 'arrears' | 'pot_total';

export type FundingOverridePayload =
    | { purpose: FundingPurpose; self_approved: true }
    | { purpose: FundingPurpose; approval_id: string; code: string };

export interface PendingFundingApproval {
    approvalId: string;
    approverName: string;
    approverEmail: string;
    purpose: FundingPurpose;
    shortfallGross: number;
    expiresAt: string;
}

export interface SubmitInvoicePayload {
    invoice_number: string;
    invoiced_at: string;
    invoicing_dealer_id: string;
    mileage: number;
    lines: InvoiceLinePayload[];
    funding_override?: FundingOverridePayload;
}

export interface InvoiceRejectionReason {
    line: number | null;
    code: string;
}

export type RejectionRecorder = (agreementId: string, reasons: InvoiceRejectionReason[]) => void;

export interface OwnDealerLiveAgreement {
    id: string;
    agreementRef: string;
    status: AgreementStatus;
}

export interface VehicleAgreementCheck {
    hasLiveAgreement: boolean;
    ownDealer: OwnDealerLiveAgreement[];
    otherDealer: boolean;
}

export interface VehicleAgreementCheckQuery {
    vin?: string;
    vrm?: string;
}
